/* Lacinia Herbaryum 2026 — page compositions.
   Every page is composed on a 210 × 210 mm square in CSS mm (print units). Pictures that reach the trim run 3 mm past it.
   The book is one ordered PLAN; page numbers, specimen numbers (No.), cross references and the index all follow from it.
   Every text comes from LH.content[lang]; facts (codes, sizes) from LH.products. Nothing language-specific here.
   Layout positions are written from the spine outwards (u = mm from the inner edge) and mirrored for left pages. */
window.LH = window.LH || {};
(function () {
  LH.VERSION = '2026-10-27.1';   // labels, index and legend carry the code and the quantity only (Alp 2026-10-27); 2026-10-26.1 was the team review 2026-10-23: quantities, SPF 50+, p. 117 labels, family page numbers, Turkish capitals
  let T;
  const M = LH.marks;

  /* ---------- sources ---------- */
  const base = () => LH.imgBase || 'assets/img/lo';
  /* on the store (window.LH_SITE) every picture is a Shopify File: the book path is looked up in LH_SITE.files */
  const at_ = (p) => (window.LH_SITE && LH_SITE.files && LH_SITE.files[p]) || p;
  const sc = (n) => at_(`${base()}/sc/${n}.${LH.scExt || 'webp'}`);
  const pk = (sku) => at_(`${base()}/pk/${sku}.${LH.pkExt || 'webp'}`);
  const brand = (f) => at_(`${base()}/brand/${f}`);
  const P = (h) => LH.products[h];
  const C = (h) => T.p[h];
  /* the two tanning oils are two products (review 2026-10-20: «aynı ürün değil: Bronze tan – bronzlaştırıcı yağ, Shimmer tan – simli
     bronzlaştırıcı yağ»). The store sells them as one product with two options, so the book splits them here, before the SKU map:
     Shimmer Tan (NRM166) becomes its own specimen with its own number, marker and index row */
  if (LH.products['tanning-oil'] && !LH.products['shimmer-tan']) {
    const t = LH.products['tanning-oil'];
    LH.products['shimmer-tan'] = { ...t, v: t.v.filter(([sku]) => sku === 'NRM166') };
    t.v = t.v.filter(([sku]) => sku !== 'NRM166');
    if (LH.real && LH.real['tanning-oil']) LH.real['shimmer-tan'] = LH.real['tanning-oil'];
  }
  const SKU = {};
  Object.entries(LH.products).forEach(([h, p]) => p.v.forEach(([s]) => { SKU[s] = h; }));
  const dims = (k) => (LH.dims && LH.dims[k]) || [1000, 1000];
  const ratio = (sku) => { const d = dims('pk:' + sku); return d[0] / d[1]; };
  const qty = (h) => P(h).size || (P(h).form ? T.forms[P(h).form] : '');

  /* ---------- geometry ---------- */
  const L = (s, u, w = 0) => (s === 'R' ? u : 210 - u - w);         // left edge of a box placed u mm from the spine
  const at = (s, u, y, w, h) => `left:${L(s, u, w).toFixed(2)}mm;top:${y}mm;width:${w}mm${h != null ? `;height:${h}mm` : ''}`;
  const sideOf = (n) => (n % 2 === 0 ? 'L' : 'R');

  /* ---------- small type on photographs: light or dark ink, read from the picture's brightness grid (LH.lum) ---------- */
  const B36 = '0123456789abcdefghijklmnopqrstuvwxyz';
  const FULL = { x: -3, y: -3, w: 216, h: 216 };
  /* page point (mm) → fraction of the picture under it, when the picture covers box {x, y, w, h} (object-fit: cover) */
  function coverInv(img, box, pos) {
    const [iw, ih] = dims(img);
    const k = Math.max(box.w / iw, box.h / ih), W = iw * k, H = ih * k;
    const [px, py] = (pos || '50% 50%').split(' ').map((v) => parseFloat(v) / 100);
    const x0 = box.x + (box.w - W) * px, y0 = box.y + (box.h - H) * py;
    return (x, y) => [(x - x0) / W, (y - y0) / H];
  }
  /* mean brightness, 0 (black) to 1 (white), of the picture under the page rectangle r = [x0, y0, x1, y1] */
  function bright(img, box, pos, r) {
    const g = (LH.lum || {})[img]; if (!g) return 1;
    const [cols, rows, str] = g, inv = coverInv(img, box, pos);
    let sum = 0, n = 0;
    for (let i = 0; i <= 6; i++) for (let j = 0; j <= 2; j++) {
      const [fx, fy] = inv(r[0] + (r[2] - r[0]) * i / 6, r[1] + (r[3] - r[1]) * j / 2);
      const c = Math.min(cols - 1, Math.max(0, Math.floor(fx * cols))), w = Math.min(rows - 1, Math.max(0, Math.floor(fy * rows)));
      sum += B36.indexOf(str[w * cols + c]) / 35; n++;
    }
    return sum / n;
  }
  const isDark = (img, box, pos, r) => bright(img, box, pos, r) < .56;

  /* ---------- small parts ---------- */
  /* a page number is plain text: the pages carry no links, a press anywhere on the book turns the page (Alp, 2026-10-19:
     a press that landed on a number or a contents tag jumped there, which read as a random jump) */
  const pnum = (n, txt) => `<span class="pl">${txt == null ? n : txt}</span>`;
  const pref = (n) => `${T.meta.pageAbbr}&nbsp;${pnum(n)}`;
  const eyebrow = (ch, extra) => `<p class="eyebrow"><b>${T.ch[ch].no}</b> · ${T.ch[ch].part} — ${extra || T.ch[ch].area}</p>`;
  const swatch = (hex) => (hex ? `<i class="sw" style="background:${hex}"></i>` : '');
  /* a series is told in one place (Alp, 2026-10-18: «bazı ürünler seri olmasına rağmen … onları birlikte anlat»): the four scent
     families live in Çiçek, Fulvicol and the sun products in Vücut, the green series (with its foot spray) in Yaprak, the lash oil
     beside the mascara, so these products take that chapter's number on their labels */
  Object.entries({ 'dus-jeli': 'cicek', 'el-ve-vucut-losyonu': 'cicek', 'keratin-sutu': 'cicek', 'fulvicol-saw-palmetto-sampuan': 'govde',
    'koku-onleyici-ayak-spreyi': 'yaprak', 'leke-karsiti-gunes-kremi-spf50': 'govde', 'kas-kirpik-bakim-yagi': 'cicek' })
    .forEach(([h, ch]) => { if (LH.products[h]) LH.products[h].ch = ch; });

  /* a packshot standing on (cu, by): centre u from the spine, bottom y; h = height in mm */
  function shot(s, sku, cu, by, h, o = {}) {
    const w = h * ratio(sku);
    const x = s === 'R' ? cu : 210 - cu;
    return `<div class="pkw" style="left:${x.toFixed(2)}mm;top:${by}mm;${o.z ? `z-index:${o.z}` : ''}">
      ${o.noShadow ? '' : `<i class="cs" style="width:${(w * 1.18).toFixed(1)}mm;height:${Math.max(2.2, w * .16).toFixed(1)}mm"></i>`}
      <img src="${pk(sku)}" style="height:${h.toFixed(1)}mm" alt="${o.alt || ''}" decoding="async"></div>`;
  }
  /* the variants of one product standing side by side on one line, never overlapping, read left to right, each with its name
     beneath; centred on cu (mm from the spine), bottom at by, at most ph tall and maxW wide in all. Every variant has a slot at
     least o.minSlot wide so the names never run into each other */
  function lineup(s, h, cu, by, ph, maxW, o = {}) {
    const c = C(h), v = P(h).v, caps = o.caps !== false;
    const gap = o.gap != null ? o.gap : Math.max(3, ph * .08);
    let m = caps ? (o.minSlot || 16) : 0;
    if (v.length * Math.max(m, gap) > maxW) m = maxW / v.length;
    // packs with something thin rising above them (a diffuser's reeds, LH.body = the pack's share of its picture): the
    // packs stand equally tall, whatever the reeds do
    const bf = v.map(([sku]) => (LH.body || {})[sku]);
    const kf = bf.every((f) => f) ? bf.map((f) => Math.min(...bf) / f) : v.map(() => 1);
    const slots = (x) => v.map(([sku], i) => Math.max(x * kf[i] * ratio(sku) + gap, m));
    const span = (x) => slots(x).reduce((a, b) => a + b, 0);
    let hh = ph;
    if (span(ph) > maxW) { let lo = 0, hi = ph; for (let k = 0; k < 30; k++) { const mid = (lo + hi) / 2; if (span(mid) > maxW) hi = mid; else lo = mid; } hh = lo; }
    const sl = slots(hh);
    let x = (s === 'R' ? cu : 210 - cu) - span(hh) / 2;                         // page x, left to right
    return v.map(([sku, opt], i) => {
      const mid = x + sl[i] / 2, cw = sl[i] - 1.2; x += sl[i];
      const name = (c.vo && c.vo[opt]) || opt;
      const cap = caps ? `<p class="vcap" style="left:${(mid - cw / 2).toFixed(2)}mm;top:${(by + 2.4).toFixed(2)}mm;width:${cw.toFixed(2)}mm">${swatch(c.sw && c.sw[opt])}${name}</p>` : '';
      return shot(s, sku, s === 'R' ? mid : 210 - mid, by, hh * kf[i], { alt: `${c.n}, ${name}` }) + cap;
    }).join('');
  }
  /* products sharing a block keep their order of size (LH.real, cm), softened so a small pack never vanishes beside a big one:
     the tallest gets cap mm, every other the square root of its share, never less than 62 % */
  const realH = (h) => ((LH.real || {})[h] || [12])[0];
  const scaled = (hs, cap) => { const top = Math.max(...hs.map(realH)); return hs.map((h) => cap * Math.max(.62, Math.sqrt(realH(h) / top))); };
  /* u (mm from the spine) of slot i of n equal slots across the text width, so slots read left to right on either page */
  const slotU = (s, i, n, cw, gap) => 18 + (s === 'R' ? i : n - 1 - i) * (cw + gap);

  /* the herbarium label */
  function label(h, o = {}) {
    const p = P(h), t = T.meta, c = C(h);
    const multi = p.v.length > 1;
    const rows = multi
      ? `<tr class="hd"><th>${o.optWord || t.option}</th><th>${t.code}</th></tr>` + p.v.map(([sku, opt]) => `<tr><td class="o">${swatch(c.sw && c.sw[opt])}${(c.vo && c.vo[opt]) || opt}</td><td>${sku}</td></tr>`).join('')
      : `<tr><th>${t.code}</th><td>${p.v[0][0]}</td></tr>`;
    const q = qty(h);
    const qrow = q ? `<tr><th>${t.qty}</th><td>${q}</td></tr>` : '';
    return `<div class="lbl ${o.cls || ''}" style="${o.style || ''}">
      <p class="lh"><span class="no">${t.no}&nbsp;${LH.no[h]}</span><span class="ch">${T.ch[p.ch].no} · ${T.ch[p.ch].part}</span></p>
      <table>${multi ? rows + qrow : rows + qrow}</table></div>`;
  }
  /* one product's text: kind · name · description · key ingredients · label */
  function spec(s, h, u, y, w, o = {}) {
    const c = C(h), p = P(h);
    return `<div class="sp" style="${at(s, u, y, w)}">
      <p class="kd">${c.k}${p.size && !o.noSize ? ` · ${p.size}` : ''}</p>
      <h3 class="nm ${o.big ? 'l' : o.small ? 's' : ''}">${c.n}</h3>
      ${o.noDesc ? '' : `<p class="ds">${o.short && c.d2 ? c.d2 : c.d}</p>`}
      ${c.i && !o.noIng ? `<p class="ing">${c.i}</p>` : ''}
      ${o.use && c.u ? `<p class="use"><b>${T.meta.use}</b> ${c.u}</p>` : ''}
      ${o.noLabel ? '' : label(h, o.lbl)}</div>`;
  }
  /* «{at:<handle>}» in a text: the page of that product, from the plan (never a typed number) */
  const atRefs = (t) => t.replace(/\{at:([\w-]+)\}/g, (_, h) => pref(LH.at[h]));
  function head(s, key, ch, o = {}) {
    const c = T.pg[key] || {};
    return `<div class="sec" style="${at(s, o.u || 18, o.y || 15, o.w || 124)}">${eyebrow(ch, c.e)}
      ${c.h ? `<h2 class="h2">${c.h}</h2>` : ''}${c.s ? `<p class="sub">${atRefs(c.s)}</p>` : ''}</div>`;
  }

  /* pictures */
  const full = (img, pos = '50% 50%') => `<div class="full"><img src="${sc(img)}" style="object-position:${pos}" alt="" decoding="async"></div>`;
  const photo = (s, img, u, y, w, h, pos = '50% 50%', extra = '') => `<div class="photo" style="${at(s, u, y, w, h)};${extra}"><img src="${sc(img)}" style="object-position:${pos}" alt="" decoding="async"></div>`;
  const spreadImg = (img, half, pos = '50% 50%') => `<div class="spread ${half}"><img src="${sc(img)}" style="object-position:${pos}" alt="" decoding="async"></div>`;

  /* where a point of a picture lands when the picture covers a box (object-fit: cover + object-position) */
  function coverMap(img, bw, bh, pos) {
    const [iw, ih] = dims(img);
    const k = Math.max(bw / iw, bh / ih), W = iw * k, H = ih * k;
    const [px, py] = (pos || '50% 50%').split(' ').map((v) => parseFloat(v) / 100);
    const x0 = (bw - W) * px, y0 = (bh - H) * py;
    return (fx, fy) => [x0 + fx * W, y0 + fy * H];
  }
  /* the object-position that centres a photograph's products (LH.boxes, with room for their numbers above them) in the box the
     photograph is cropped to: 'auto' in the plan, so a new take of a scene never needs its crop set by hand */
  function autoPos(img, bw, bh) {
    const B = Object.values((LH.boxes || {})[img] || {}); if (!B.length) return '50% 50%';
    const [iw, ih] = dims(img), k = Math.max(bw / iw, bh / ih), W = iw * k, H = ih * k;
    const fit = (lo, hi, span, win) => (span - win < .5 ? 50 : 100 * Math.max(0, Math.min(span - win, (lo + hi) / 2 - win / 2)) / (span - win));
    const py = fit(Math.min(...B.map((b) => b[0])) / 1000 * H - 9, Math.max(...B.map((b) => b[2])) / 1000 * H + 2, H, bh);
    const px = fit(Math.min(...B.map((b) => b[1])) / 1000 * W - 2, Math.max(...B.map((b) => b[3])) / 1000 * W + 2, W, bw);
    return `${px.toFixed(1)}% ${py.toFixed(1)}%`;
  }
  /* specimen numbers pinned over the products of a scene. box = the picture's box in page mm (x, y, w, h). */
  function markers(img, box, pos, o = {}) {
    const B = (LH.boxes || {})[img]; if (!B) return '';
    const map = coverMap(img, box.w, box.h, pos), [iw, ih] = dims(img);
    const seen = {};
    return Object.entries(B).map(([sku, b]) => {
      const h = SKU[sku]; if (!h || LH.no[h] == null || (o.skip || []).includes(sku)) return '';
      /* a standing pack carries its number above its top; one lying down (much wider than tall) at its middle */
      const lying = (b[3] - b[1]) * iw > 1.3 * (b[2] - b[0]) * ih;
      const f = (o.mk || {})[sku] || (lying ? [(b[1] + (b[3] - b[1]) * .62) / 1000, (b[0] + (b[2] - b[0]) * .5) / 1000] : [(b[1] + b[3]) / 2000, b[0] / 1000]);
      let [x, y] = map(f[0], f[1]);
      let py = box.y + y - (o.lift == null ? 5 : o.lift);
      if (py < 8) { y = map(f[0], (b[0] + (b[2] - b[0]) * .3) / 1000)[1]; py = box.y + y - (o.lift == null ? 5 : o.lift); }   // top cut by the frame: pin it to the upper body
      const px = box.x + x;
      if (px < 6 || px > 204 || py < 8 || py > 196) return '';
      const key = `${h}:${Math.round(px / 9)}:${Math.round(py / 9)}`; if (seen[key]) return ''; seen[key] = 1;
      return `<span class="mk" style="left:${px.toFixed(1)}mm;top:${py.toFixed(1)}mm">${LH.no[h]}</span>`;
    }).join('');
  }
  /* the photo's card: which specimens are in the picture, and where they are described */
  function pcard(img, style, o = {}) {
    const B = (LH.boxes || {})[img] || {};
    const hs = []; Object.keys(B).forEach((sku) => { const h = SKU[sku]; if (h && LH.no[h] != null && !hs.includes(h)) hs.push(h); });
    hs.sort((a, b) => LH.no[a] - LH.no[b]);
    const cap = T.scenes[img];
    /* the four scent families share their five products' handles: a family's picture points to that family's own page
       (review 2026-10-23: pp. 98, 100, 102 all said p. 97) */
    const fam = /^sc-fam-(\w+)$/.exec(img), pgOf = (h) => (fam && LH.famAt[fam[1]]) || LH.at[h];
    return `<div class="pcard" style="${style}"><h5>${T.meta.inPhoto}</h5><ul>${hs.map((h) => `<li><i>${LH.no[h]}</i><span>${C(h).n}</span><em>${pnum(pgOf(h))}</em></li>`).join('')}</ul>
      ${cap && !o.noCap ? `<p class="cap">${cap}</p>` : ''}</div>`;
  }

  /* ================================================================ templates */
  const R = {};

  /* the cover, with no product (Alp, 2026-10-19: «kapakta da ürün olması gerekmiyor güzel bir çalışma yap»): the five parts of a
     plant that name the book's five chapters, pressed on deep green herbarium paper, each with its chapter set beneath it in cream
     like a herbarium label. LH.COVER: the specimens' centres in the picture (mm from the left trim) and the labels' top line */
  R.cover = () => `
    ${full('cover', '50% 50%')}
    <img class="cover-logo" src="${brand('lacinia-logo-mono-cream.svg')}" style="left:86mm;top:17mm;width:38mm" alt="LACINIA">
    <div class="cover-t" style="left:105mm;top:34mm">
      <p class="k">${T.cover.kicker}</p>
      <h1>${T.cover.title}</h1>
    </div>
    ${['kok', 'govde', 'yaprak', 'cicek', 'tohum'].map((ch, i) => `<div class="cover-part" style="left:${LH.COVER.x[i]}mm;top:${LH.COVER.y}mm">
      <p class="n">${T.ch[ch].no}</p><p class="p">${T.ch[ch].part}</p><p class="a">${T.ch[ch].area}</p></div>`).join('')}`;

  R.howto = (o, n, s) => {
    const h = 'argan-yagli-sampuan', t = T.howto;
    const sku = P(h).v[0][0], ph = 74, cu = 44, top = 134 - ph;
    return `
    <div class="sec" style="${at(s, 18, 15, 150)}"><p class="eyebrow">${t.eyebrow}</p><h2 class="h2">${t.title}</h2><p class="sub">${t.sub}</p></div>
    ${shot(s, sku, cu, 134, ph)}
    <span class="mk" style="left:${(s === 'R' ? cu : 210 - cu).toFixed(1)}mm;top:${top - 4.9}mm">${LH.no[h]}</span>
    <div class="howto-lbl" style="${at(s, 84, 52, 108)}">${label(h, { cls: 'big' })}</div>
    <div class="howto-keys" style="${at(s, 84, 92, 108)}">${t.parts.map((x) => `<div><p class="eyebrow"><b>${x[0]}</b></p><p class="body">${x[1]}</p></div>`).join('')}</div>
    <div class="note" style="${at(s, 18, 156, 176)}"><p>${t.pageRefs}</p></div>`;
  };

  R.title = (o, n, s) => `
    <div class="abs" style="${at(s, 18, 40, 170)}">
      <img src="${brand('lacinia-logo-v2-dark.svg')}" style="width:58mm" alt="LACINIA">
      <h1 class="h1" style="font-size:58pt;margin-top:14mm;letter-spacing:-.02em">${T.title.title}</h1>
      <p class="lead" style="margin-top:4mm;max-width:120mm">${T.title.sub}</p>
    </div>
    <div class="abs" style="${at(s, 18, 150, 170)}">
      <p class="latin" style="font-size:14pt;color:var(--ink-2)">${T.title.motto}</p>
      <p class="eyebrow" style="margin-top:3mm">${T.title.meta}</p>
    </div>`;

  /* contents: the pressed calendula across the spread, a label pinned to each part of the plant */
  R.toc = (o) => {
    const half = o.half, off = half === 'L' ? 0 : 210;
    const tags = T.toc.tags.map((g) => ({ ...g, ...LH.TOC[g.ch] }));
    const mine = tags.filter((g) => (half === 'L' ? g.x < 210 : g.x >= 210));
    const lines = tags.filter((g) => (half === 'L' ? Math.min(g.x, g.px) < 210 : Math.max(g.x + 46, g.px) > 210));
    return `
    ${spreadImg('toc-plant', half, '50% 55%')}
    ${half === 'L' ? `<div class="toc-h" style="left:16mm;top:15mm;width:120mm"><p class="eyebrow">${T.toc.eyebrow}</p><h2 class="h1" style="margin-top:2mm">${T.toc.title}</h2><p class="sub" style="font:italic 400 10.4pt/1.35 var(--f-d);color:var(--ink-2);margin-top:2mm;max-width:110mm">${T.toc.sub}</p></div>` : ''}
    <svg class="toc-line" style="left:${-off}mm;top:0;width:420mm;height:210mm" viewBox="0 0 420 210">${lines.map((g) => `<path d="M${g.px} ${g.py} L${g.lx} ${g.ly}"/><circle cx="${g.px}" cy="${g.py}" r="1"/>`).join('')}</svg>
    ${mine.map((g) => `<div class="toc-tag" style="left:${g.x - off}mm;top:${g.y}mm;--accent:var(--${g.ch})">
      <span class="t-no">${T.ch[g.ch].no}</span><span class="t-pg">${LH.chStart[g.ch]}</span>
      <p class="t-part">${T.ch[g.ch].part}</p><p class="t-area">${T.ch[g.ch].area}</p><p class="t-sub">${g.sub}</p></div>`).join('')}
    ${half === 'R' ? `<ul class="toc-more" style="left:${LH.TOC.more.x - 210}mm;top:${LH.TOC.more.y}mm;width:62mm">${T.toc.more.map(([txt, key]) => `<li><span>${txt}</span><span>${pnum(LH.anchor[key])}</span></li>`).join('')}</ul>` : ''}`;
  };

  /* where a full-page photograph's card goes: the corner where it (and the plate card, in another corner) hides none of the
     products - every product's box on the page with 2 mm of air round it and the place of its number above it; the outer corners
     first. Where every corner hides something the card loses its caption and tries again, and where even that hides something
     there is no card (the products' own page faces the photograph); the plate card then takes the freest corner. */
  function quietCorner(img, box, pos, s, plate) {
    const B = (LH.boxes || {})[img] || {};
    const map = coverMap(img, box.w, box.h, pos);
    const rects = Object.values(B).map((b) => { const [x0, y0] = map(b[1] / 1000, b[0] / 1000), [x1, y1] = map(b[3] / 1000, b[2] / 1000);
      return [box.x + x0 - 2, box.y + y0 - 9, box.x + x1 + 2, box.y + y1 + 2]; });
    const n = Object.keys(B).filter((sku) => SKU[sku]).length, cap = T.scenes[img] || '';
    const w = 57.6, pw = 59.6, ph = 84, FREE = 6;
    const hgt = (withCap) => 8 + 3.9 * n + (withCap && cap ? 4 + 3.9 * Math.ceil(cap.length / 40) : 0);
    const hit = (x, y, cw, ch) => rects.reduce((a, r) => a + Math.max(0, Math.min(x + cw, r[2]) - Math.max(x, r[0])) * Math.max(0, Math.min(y + ch, r[3]) - Math.max(y, r[1])), 0);
    const plateCost = (c) => (plate ? hit(c[1] === 'l' ? 12 : 198 - pw, c[0] === 't' ? 12 : 192 - ph, pw, ph) : 0);
    const order = s === 'L' ? ['bl', 'tl', 'br', 'tr'] : ['br', 'tr', 'bl', 'tl'];
    // the plate card takes a corner of its own; the photo card goes in any other corner
    const pairs = plate ? order.flatMap((c) => order.filter((p) => p !== c).map((p) => [c, p])) : order.map((c) => [c, null]);
    const cost = ([c, p], h) => hit(c[1] === 'l' ? 12 : 198 - w, c[0] === 't' ? 12 : 192 - h, w, h) + (p ? plateCost(p) : 0);
    const pick = (h) => pairs.reduce((best, q) => (cost(q, h) < cost(best, h) - 1 ? q : best), pairs[0]);
    for (const withCap of [true, false]) {
      const q = pick(hgt(withCap));
      if (cost(q, hgt(withCap)) <= FREE) return { corner: q[0], cap: withCap, plate: q[1] };
    }
    return { none: true, corner: order[0], plate: order.reduce((a, c) => (plateCost(c) < plateCost(a) - 1 ? c : a), order[0]) };
  }

  R.scene = (o, n, s) => {
    const pos = o.pos === 'auto' ? autoPos(o.img, 216, 216) : o.pos || '50% 50%';
    const box = { x: -3, y: -3, w: 216, h: 216 };
    const q = o.card ? { corner: o.card, cap: true, plate: `t${o.card[1] === 'l' ? 'r' : 'l'}` } : quietCorner(o.img, box, pos, s, !!o.plateCard);
    const at4 = { tl: 'left:12mm;top:12mm', tr: 'right:12mm;top:12mm', bl: 'left:12mm;bottom:18mm', br: 'right:12mm;bottom:18mm' };
    const cs = at4[q.corner], pc = o.plateCard ? plateCard(o.plateCard, at4[q.plate]) : '';
    return `${full(o.img, pos)}${o.marks === false ? '' : markers(o.img, box, pos, o)}
      ${o.card === false || q.none ? '' : pcard(o.img, cs, { ...o, noCap: o.noCap || !q.cap })}${pc}`;
  };

  /* a plate laid on a photograph as a specimen card: the pressed plant, its names and its products */
  function plateCard(key, style) {
    const t = T.plates[key];
    return `<div class="pcard plate-card" style="${style};width:58mm">${`<div class="photo" style="position:relative;width:100%;height:36mm"><img src="${sc('pl-' + key)}" style="object-position:50% 50%" alt="" decoding="async"></div>`}

      <h5 style="margin-top:2.2mm">${T.meta.plate}</h5><p class="h3" style="font-size:13pt">${t.tr}</p><p class="latin" style="font-size:9pt">${t.la}</p>
      <p class="body" style="font-size:6.4pt;margin-top:1.2mm">${t.short}</p>
      <p class="body" style="font-size:6pt;margin-top:1.2mm;color:var(--mute)">${T.meta.inBook}: ${LH.plateUse[key].map((h) => `${C(h).n}&nbsp;${pnum(LH.at[h])}`).join(' · ')}</p></div>`;
  }

  R.manifesto = (o, n, s) => {
    const t = T.manifesto;
    return `
    <div class="abs" style="${at(s, 18, 18, 168)}"><p class="eyebrow">${t.eyebrow}</p>
      <h2 class="h1" style="margin-top:3mm">${t.title}</h2></div>
    <div class="abs" style="${at(s, 18, 62, 98)}">${t.body.map((p) => `<p class="lead s" style="margin-bottom:3mm">${p}</p>`).join('')}</div>
    <div class="abs" style="${at(s, 128, 62, 58)}">${t.principles.map(([h, p]) => `<div style="padding:2.6mm 0;border-top:.2mm solid var(--line)"><p class="h3">${h}</p><p class="body" style="margin-top:1mm">${p}</p></div>`).join('')}</div>
    <div class="abs" style="${at(s, 18, 170, 168)}"><p class="latin" style="font-size:12pt;color:var(--ink-2)">${t.sign}</p></div>`;
  };

  /* need map: a botanist's key — five columns, one per plant part */
  R.needmap = (o, n, s) => {
    const cols = o.half === 'L' ? ['kok', 'govde'] : ['yaprak', 'cicek', 'tohum'];
    const t = T.needmap;
    const xs = o.half === 'L' ? [[16, 82], [106, 82]] : [[18, 56], [80, 56], [142, 52]];
    const w = (o.half === 'L');
    return `
    ${w ? `<div class="abs" style="left:16mm;top:15mm;width:170mm"><p class="eyebrow">${t.eyebrow}</p><h2 class="h1" style="margin-top:2mm">${t.title}</h2><p class="sub" style="font:italic 400 10.4pt/1.35 var(--f-d);color:var(--ink-2);margin-top:2mm;max-width:150mm">${t.sub}</p></div>` : ''}
    ${cols.map((ch, i) => `<div class="key-col" style="left:${xs[i][0]}mm;width:${xs[i][1]}mm;--c:var(--${ch})">
      <p class="kc-h"><b>${T.ch[ch].no}</b><span>${T.ch[ch].part}</span><em>${T.ch[ch].area}</em></p>
      <ul>${t[ch].map(([need, hs]) => `<li><h5>${need}</h5><p>${hs.map((h) => `${pnum(LH.at[h], `${C(h).n}&nbsp;<em>${LH.at[h]}</em>`)}`).join(' · ')}</p></li>`).join('')}</ul></div>`).join('')}`;
  };

  R.opener = (o) => {
    const ch = o.ch, half = o.half, t = T.ch[ch], g = LH.OPEN[ch];
    const off = half === 'L' ? 0 : 210, box = { x: -3 - off, y: -3, w: 426, h: 216 };
    const here = (b) => b && b[0] >= off && b[0] < off + 210;
    const place = (b) => `left:${b[0] - off}mm;top:${b[1]}mm;width:${b[2]}mm`;
    const list = `<ul class="op-list">${LH.chSections[ch].map(([txt, pg]) => `<li><b>${txt}</b><span>${pnum(pg)}</span></li>`).join('')}</ul>
      <p class="op-count">${LH.chCount[ch]} ${T.meta.productsWord} · ${T.meta.pageAbbr} ${LH.chStart[ch]}–${LH.chEnd[ch]}</p>`;
    return `
    ${spreadImg(g.img, half, g.pos)}
    ${here(g.t) ? `<div class="op-t" style="${place(g.t)}"><p class="op-no">${t.no}</p><p class="op-part">${t.part}</p><p class="op-area">${t.area}</p><p class="op-latin">${t.latin}</p></div>` : ''}
    ${here(g.i) ? `<div class="op-d" style="${place(g.i)}"><p class="op-intro">${t.intro}</p>${g.l ? '' : list}</div>` : ''}
    ${here(g.l) ? `<div class="op-d" style="${place(g.l)}">${list}</div>` : ''}`;
  };

  /* ---------- product layouts ---------- */
  /* Every product stands on a line (a shelf, with its contact shadow), never in the air; products sharing a shelf keep their
     relative size (scaled); several products read left to right in the order of their specimen numbers. */
  const LAY = {};
  const shelfLine = (s, u, y, w) => `<div class="rule shelf" style="${at(s, u, y, w)}"></div>`;
  /* the packs stand on the paper with their own soft shadow; the rule that closes their row runs AIR mm below their base, or
     under their names (Alp, 2026-10-18: «görselleri çizgiye sıfır yapmışsın») */
  const AIR = 4.5;

  /* a seed box: compartments for small, related items (ampoule cards); the packs as large as the compartment allows */
  LAY.cabinet = (s, items, o = {}) => {
    const cols = o.cols || 3, rows = Math.ceil(items.length / cols);
    const w = 176, y = 46, hgt = 192 - y, cw = w / cols, chh = hgt / rows;
    const cells = items.map((h, i) => {
      const c = C(h), sku = P(h).v[0][0];
      const col = i % cols, row = Math.floor(i / cols);
      const x0 = L(s, 18, w) + col * cw;                                        // page x of the compartment, left to right
      return shot('R', sku, x0 + cw / 2, y + row * chh + chh * .66, Math.min(chh * .6, cw * .76 / ratio(sku)), { alt: c.n })
        + `<div class="sp" style="left:${(x0 + 2.6).toFixed(2)}mm;top:${(y + row * chh + chh * .695).toFixed(2)}mm;width:${(cw - 5.2).toFixed(2)}mm">
          <p class="nm s" style="margin:0;font-size:10.4pt">${c.n}</p><p class="ds" style="margin-top:.6mm;font-size:6pt">${c.d2 || c.d}</p>
          <p class="kd" style="margin-top:.9mm;letter-spacing:.06em;text-transform:none;font-size:5.2pt">${T.meta.no}&nbsp;${LH.no[h]} · ${sku}${qty(h) ? ` · ${qty(h)}` : ''}</p></div>`;
    }).join('');
    const grid = Array.from({ length: cols - 1 }, (_, i) => `<div class="vrule" style="left:${(L(s, 18, w) + (i + 1) * cw).toFixed(2)}mm;top:${y}mm;height:${hgt}mm"></div>`).join('')
      + Array.from({ length: rows - 1 }, (_, i) => `<div class="rule" style="left:${L(s, 18, w)}mm;top:${y + (i + 1) * chh}mm;width:${w}mm"></div>`).join('');
    return `<div class="abs" style="left:${L(s, 18, w)}mm;top:${y}mm;width:${w}mm;height:${hgt}mm;border:.2mm solid var(--ink-2)"></div>${grid}${cells}`;
  };

  /* ---------- product pages, 2026-10-18 (Alp: «tekli ürünlerde görsellerin kompozisyonda bozuk ve çok ufak olmaları problem»):
     every pack as large as its place allows, standing on a shelf line with its text beneath it; a product alone stands large
     beside its text; a product's variants in one, two or three rows, whichever shows them larger */

  /* the variants of one product in a box (u from the spine, y, w, hgt; the names, and a product's guide to choosing a variant,
     take the box's last cap mm); o.center sizes the rows to the packs and centres them in the box */
  function variantGrid(s, h, u, y, w, hgt, o = {}) {
    const c = C(h), v = P(h).v, guide = c.vg && o.caps !== false, cap = o.caps === false ? 2 : guide ? 19 : 8.5, gap = o.gap || 6, rgap = 5;
    const bf = v.map(([sku]) => (LH.body || {})[sku]);
    const kf = bf.every((f) => f) ? bf.map((f) => Math.min(...bf) / f) : v.map(() => 1);
    const a = Math.max(...v.map(([sku], i) => ratio(sku) * kf[i]));
    let best = null;
    for (let r = 1; r <= Math.min(3, v.length); r++) {
      const cols = Math.ceil(v.length / r), cw = (w - gap * (cols - 1)) / cols, rh = (hgt - rgap * (r - 1)) / r;
      const ph = Math.min(rh - cap - 4, cw * .88 / a, o.ph || 999);
      if (!best || ph > best.ph + 1) best = { cols, cw, rh, ph, r };
    }
    const { cols, cw, ph, r } = best;
    const rh = o.center ? ph + cap + 4 : best.rh, y0 = o.center ? y + (hgt - r * rh - (r - 1) * rgap) / 2 : y;
    let out = '';
    for (let row = 0; row < r; row++) {
      const inRow = Math.min(cols, v.length - row * cols); if (inRow <= 0) break;
      const off = (w - (inRow * cw + (inRow - 1) * gap)) / 2, by = y0 + row * (rh + rgap) + rh - cap;
      out += shelfLine(s, u + off, by + Math.max(AIR, cap - 1.5), inRow * cw + (inRow - 1) * gap);
      for (let k = 0; k < inRow; k++) {
        const i = row * cols + k, [sku, opt] = v[i];
        const u0 = u + off + (s === 'R' ? k : inRow - 1 - k) * (cw + gap), name = (c.vo && c.vo[opt]) || opt;
        const x = s === 'R' ? u0 : 210 - u0 - cw;
        out += shot(s, sku, u0 + cw / 2, by, ph * kf[i], { alt: `${c.n}, ${name}` })
          + (o.caps === false ? '' : `<p class="vcap" style="left:${x.toFixed(2)}mm;top:${(by + 2.4).toFixed(2)}mm;width:${cw.toFixed(2)}mm">${swatch(c.sw && c.sw[opt])}${name}</p>`)
          + (guide && c.vg[opt] ? `<p class="vgd" style="left:${x.toFixed(2)}mm;top:${(by + 6.2).toFixed(2)}mm;width:${cw.toFixed(2)}mm">${c.vg[opt]}</p>` : '');
      }
    }
    return out;
  }
  /* one product on a page: the pack (or its variants) large in one column, its text beside it */
  function hero(s, h, o = {}) {
    const p = P(h), many = p.v.length > 1, pw = o.pw || 90, by = o.by || 188, top = o.top || 34, sku = p.v[0][0];
    const tu = 18 + pw + 10, tw = 176 - pw - 10;
    const pics = many ? variantGrid(s, h, 18, top, pw, by - top + 8.5)
      : shelfLine(s, 18, by + AIR, pw) + shot(s, sku, 18 + pw / 2, by, Math.min(by - top - 4, pw * .9 / ratio(sku)), { alt: C(h).n });
    return pics + spec(s, h, tu, o.ty || 40, tw, { big: true, use: true, long: true });
  }
  /* the products of a page side by side, one row up to five, two rows beyond: each as large as its column allows, all on one
     shelf line with their text beneath; one product alone is a hero */
  LAY.cols = (s, items, o = {}) => {
    const n = items.length;
    if (n === 1) return hero(s, items[0], o);
    const rows = n > 5 ? [items.slice(0, Math.ceil(n / 2)), items.slice(Math.ceil(n / 2))] : [items];
    const per = Math.max(...rows.map((r) => r.length)), gap = per > 3 ? 6 : 9, cw = (176 - gap * (per - 1)) / per;
    const lift = items.some((h) => P(h).v.length > 1) ? 7 : AIR;     // the rule under the packs, clear of them (names under variants)
    const textH = (o.textH || (rows.length > 1 ? 36 : { 2: 50, 3: 56, 4: 64, 5: 69 }[per]) + (items.some((h) => P(h).v.length > 1) ? 6 : 0)) + lift - 2;
    const top = o.top || 50, H = (194 - top) / rows.length, ph = H - textH - 6;
    const hs = scaled(items, ph);
    // packs of one kind (same type and size) share one scale, the smallest any of them needs to fit its column
    const fit = items.map((h, i) => (P(h).v.length > 1 ? 1 : Math.min(1, cw * .86 / ratio(P(h).v[0][0]) / hs[i])));
    const kind = (h) => `${P(h).type}|${P(h).size || P(h).form || ''}`;
    const k = items.map((h) => Math.min(...items.filter((g) => kind(g) === kind(h)).map((g) => fit[items.indexOf(g)])));
    return rows.map((row, ri) => {
      const by = top + ri * H + H - textH;
      const off = (176 - (row.length * cw + (row.length - 1) * gap)) / 2;
      return shelfLine(s, 18 + off, by + lift, 176 - 2 * off) + row.map((h, j) => {
        const u0 = 18 + off + (s === 'R' ? j : row.length - 1 - j) * (cw + gap), i = items.indexOf(h), sku = P(h).v[0][0];
        const many = P(h).v.length > 1;
        const pics = many ? lineup(s, h, u0 + cw / 2, by, hs[i], cw, { minSlot: 12 })
          : shot(s, sku, u0 + cw / 2, by, hs[i] * k[i], { alt: C(h).n });
        const sp = { small: per > 2 || rows.length > 1, short: per > 2 || rows.length > 1 || !o.long, noIng: rows.length > 1 };
        return pics + (o.labelY != null
          ? spec(s, h, u0, by + lift + 3.5, cw, { ...sp, noLabel: true }) + `<div class="sp" style="${at(s, u0, o.labelY, cw)}">${label(h, { cls: 'wide', style: 'margin-top:0' })}</div>`
          : spec(s, h, u0, by + lift + 3.5, cw, sp));
      }).join('');
    }).join('');
  };
  /* one product's variants across the page, as large as they go; its text and its label above them, the variants from just under
     the label (a label is about 12 mm plus 2.7 mm a variant) */
  LAY.line = (s, items, o = {}) => {
    const h = items[0], n = P(h).v.length, top = o.top || Math.max(64, 37 + (n > 1 ? 12.3 + 2.73 * n : 15));
    return spec(s, h, 18, 30, 104, { noLabel: true, use: true, long: true }) + `<div class="sp" style="${at(s, 130, 31, 64)}">${label(h, { style: 'margin-top:0' })}</div>`
      + variantGrid(s, h, 18, top, 176, 194 - top, { ...o, center: true });
  };
  /* two products with variants side by side, each in its half: its variants above, its text beneath */
  LAY.pair = (s, items, o = {}) => {
    const top = o.top || 50, textH = o.textH || 60, by = 194 - textH;
    return items.map((h, i) => {
      const u0 = slotU(s, i, 2, 84, 8);
      return variantGrid(s, h, u0, top, 84, by - top + 8.5) + spec(s, h, u0, by + 10.5, 84, { small: true, short: true });
    }).join('');
  };
  /* one product's variants in a row under its text and label, the other products beneath it in columns, each pack on one shelf line
     with its text and label under it (the eye make-up: four shades, then the liners, the mascara and the lash oil). Review 2026-10-20
     (p. 117, «yazılar üst üste gelmiş»): beside its pack a product's text had 12-25 mm, and the label tables (white-space: nowrap)
     ran over the next pack; a 39.5 mm column holds them, as on every four-up page */
  LAY.split = (s, items, o = {}) => {
    const [a, ...rest] = items, top = o.top || 50, mid = o.mid || 106;
    return spec(s, a, 18, top, 104, { small: true, short: true, noLabel: true })
      + `<div class="sp" style="${at(s, 130, top + 1, 64)}">${label(a, { style: 'margin-top:0' })}</div>`
      + variantGrid(s, a, 18, top + 26, 176, mid - top - 26, { center: true })
      + LAY.cols(s, rest, { top: mid, textH: o.textH || 54, labelY: o.labelY });
  };

  /* a plate as a specimen tag on a product page: the pressed plant beside its names and a line about it */
  function plateTag(s, key, u, y, w) {
    const t = T.plates[key];
    return `<div class="pcard plate-tag" style="${at(s, u, y, w)}"><div class="photo"><img src="${sc('pl-' + key)}" alt="" decoding="async"></div>
      <div><h5>${T.meta.plate}</h5><p class="h3">${t.tr}</p><p class="latin">${t.la}</p><p class="body">${t.short}</p></div></div>`;
  }
  /* a page of products: its head and its layout; under a head with no heading the products start higher. A plate (o.plate) stands
     in the outer top corner beside a narrower head, or, beside a lone product, under its text */
  R.products = (o, n, s) => {
    const c = T.pg[o.key] || {}, top = o.top || (o.lay === 'line' ? undefined : c.h ? (c.s ? 50 : 42) : 30);
    const lone = o.lay === 'cols' && o.items.length === 1;
    const tag = o.plate ? (lone ? plateTag(s, o.plate, 118, 150, 76) : plateTag(s, o.plate, 132, 13, 62)) : '';
    return head(s, o.key, o.ch, o.plate && !lone ? { ...(o.head || {}), w: 108 } : o.head) + LAY[o.lay](s, o.items, { ...o, top }) + tag;
  };

  R.compare = (o, n, s) => {
    const t = T.compare[o.key];
    return `${head(s, o.key, o.ch, { w: 170 })}
    <table class="tbl" style="${at(s, 18, o.y || 52, 176)}">
      <thead><tr><th>${T.meta.no}</th>${t.cols.map((c) => `<th>${c}</th>`).join('')}<th class="pg">${T.meta.pageAbbr}</th></tr></thead>
      <tbody>${t.rows.map(([h, ...cells]) => `<tr><td class="n">${LH.no[h]}</td><td class="nm">${C(h).n}</td>${cells.map((x) => `<td${/\d/.test(x) && x.length < 16 ? ' class="c"' : ''}>${x}</td>`).join('')}<td class="pg">${pnum(LH.at[h])}</td></tr>`).join('')}</tbody></table>`;
  };

  R.plate = (o, n, s) => {
    const t = T.plates[o.key];
    return `${photo(s, 'pl-' + o.key, 92, -3, 121, 150, '50% 50%')}
    <div class="abs plate-t" style="${at(s, 18, 16, 66)}">
      <p class="eyebrow">${eyebrow(o.ch, T.meta.plate).replace(/^<p class="eyebrow">|<\/p>$/g, '')}</p>
      <p class="name" style="margin-top:3mm">${t.tr}</p><p class="latin">${t.la}</p>
      <dl><dt>${T.meta.family}</dt><dd>${t.fam}</dd><dt>${T.meta.part}</dt><dd>${t.part}</dd></dl>
      <p class="body">${t.d}</p>
      <div class="in"><h5>${T.meta.inBook}</h5><ul>${LH.plateUse[o.key].map((h) => `<li><span>${C(h).n}</span>${pnum(LH.at[h])}</li>`).join('')}</ul></div></div>
    <div class="abs" style="${at(s, 92, 156, 102)}"><p class="latin" style="font-size:9.6pt;color:var(--ink-2)">${t.note}</p></div>`;
  };

  R.ritual = (o, n, s) => {
    const t = T.rituals[o.key];
    return `${head(s, o.key, o.ch, { w: 170 })}
    <ol class="steps" style="${at(s, 18, o.y || 46, o.w || 110)}">${t.steps.map((st, i) => `<li><span class="sn">${i + 1}</span><div>
      <h4>${st.h}${st.when ? `<small>${st.when}</small>` : ''}</h4><p>${st.p}</p>
      <div class="chipl">${st.items.map((h) => `<span class="chip"><i>${LH.no[h]}</i>${C(h).n}<em>${T.meta.pageAbbr}&nbsp;${LH.at[h]}</em></span>`).join('')}</div></div></li>`).join('')}</ol>
    ${t.notes ? `<div class="abs" style="${at(s, 136, o.y || 46, 58)}">${t.notes.map(([h, p]) => `<div class="note" style="position:relative;margin-bottom:3mm"><h5>${h}</h5><p>${p}</p></div>`).join('')}</div>` : ''}`;
  };

  /* a scent family (2026-10-18): its five products side by side at one scale (their true relative size, as large as the width
     allows), each column as wide as its pack, each with its code; the family's scene faces it */
  R.family = (o, n, s) => {
    const t = T.families, fam = t.list[o.fam], row = LH.FAMILY[o.fam];
    const nn = row.length, gap = 5, room = 176 - gap * (nn - 1), by = 154, sum = (a) => a.reduce((x, y) => x + y, 0);
    const hs = scaled(row.map(([h]) => h), 104), ws = row.map(([, sku], j) => hs[j] * ratio(sku));
    const k = Math.min(1, .92 * room / sum(ws)), cws = ws.map((w) => w * k + (room - sum(ws) * k) / nn);
    const U = (x, w) => (s === 'R' ? x : 210 - x - w);                   // page x, read left to right -> u from the spine
    let x = s === 'R' ? 18 : 16;
    const cols = row.map(([h, sku], j) => {
      const cw = cws[j], x0 = x; x += cw + gap;
      return shot(s, sku, U(x0 + cw / 2, 0), by, hs[j] * k, { alt: `${C(h).n}, ${fam.name}` })
        + `<div class="sp" style="${at(s, U(x0, cw), by + AIR + 3.5, cw)}"><p class="kd">${C(h).k}${P(h).size ? ` · ${P(h).size}` : ''}</p>
          <h3 class="nm s">${C(h).k2 || C(h).n}</h3>
          <p class="kd" style="margin-top:1.3mm;letter-spacing:.05em;text-transform:none;line-height:1.5">${T.meta.no}&nbsp;${LH.no[h]} · ${sku}</p></div>`;
    }).join('');
    return `<div class="sec" style="${at(s, 18, 15, 176)}">${eyebrow('cicek', t.e)}<h2 class="h2">${fam.name}</h2><p class="sub">${fam.notes}</p></div>`
      + shelfLine(s, 18, by + AIR, 176) + cols
      + (o.first ? `<p class="body abs" style="${at(s, 18, by + 33, 150)};font-size:6.6pt;color:var(--mute)">${t.s}</p>` : '');
  };

  R.moments = (o, n, s) => {
    const t = T.moments, list = o.half === 'L' ? t.list.slice(0, 4) : t.list.slice(4);
    const pos = o.half === 'L' ? [[18, 60], [110, 60], [18, 128], [110, 128]] : [[18, 34], [110, 34], [18, 112], [110, 112]];
    return `${o.half === 'L' ? `<div class="sec" style="${at(s, 18, 15, 176)}"><p class="eyebrow">${t.e}</p><h2 class="h2">${t.h}</h2><p class="sub">${t.s}</p></div>` : ''}
    ${list.map((m, i) => `<div class="mo" style="${at(s, s === 'R' ? pos[i][0] : 128 - pos[i][0], pos[i][1], 84)}"><p class="mo-t">${m.t}</p><h4>${m.h}</h4><p>${m.p}</p>
      <div class="chipl">${m.items.map((h) => `<span class="chip"><i>${LH.no[h]}</i>${C(h).n}<em>${T.meta.pageAbbr}&nbsp;${LH.at[h]}</em></span>`).join('')}</div></div>`).join('')}`;
  };

  R.faq = (o, n, s) => `
    <div class="sec" style="${at(s, 18, 15, 176)}"><p class="eyebrow">${T.faq.e}</p><h2 class="h2">${T.faq.h}</h2></div>
    <div class="faq" style="${at(s, 18, 40, 176)}">${T.faq.list.map(([q, a]) => `<div><h5>${q}</h5><p>${a}</p></div>`).join('')}</div>`;

  R.herbs = (o, n, s) => `
    <div class="sec" style="${at(s, 18, 15, 112)}"><p class="eyebrow">${T.herbs.e}</p><h2 class="h2">${T.herbs.h}</h2><p class="sub">${T.herbs.s}</p></div>
    ${photo(s, 'index-grid', 140, 14, 54, 54, '50% 50%')}
    <table class="tbl herbs" style="${at(s, 18, 76, 176)}"><thead><tr><th>${T.herbs.cols[0]}</th><th>${T.herbs.cols[1]}</th><th class="pg">${T.herbs.cols[2]}</th></tr></thead>
      <tbody>${LH.PLATES.map((k) => `<tr><td class="nm">${T.plates[k].tr} <span class="latin">${T.plates[k].la}</span></td><td>${LH.plateUse[k].map((h) => `${C(h).n}&nbsp;<em>${LH.at[h]}</em>`).join(' · ')}</td><td class="pg">${pnum(LH.plateAt[k])}</td></tr>`).join('')}</tbody></table>`;

  R.index = (o, n, s) => {
    const rows = LH.indexPages[o.part] || [], first = o.part === 0;
    return `${first ? `<div class="sec" style="${at(s, 18, 15, 176)}"><p class="eyebrow">${T.index.e}</p><h2 class="h2">${T.index.h}</h2><p class="sub">${T.index.s}</p></div>` : ''}
    <table class="idx" style="${at(s, 18, first ? IDX.top0 : IDX.top, 176)}">
      <colgroup><col style="width:7mm"><col><col style="width:30mm"><col style="width:26mm"><col style="width:8mm"></colgroup>
      <thead><tr><th>${T.meta.no}</th><th>${T.index.cols[0]}</th><th>${T.meta.code}</th><th>${T.meta.qty}</th><th class="pg">${T.meta.pageAbbr}</th></tr></thead>
      <tbody>${rows.map((r) => r.ch ? `<tr class="ch"><td colspan="5">${T.ch[r.ch].no} · ${T.ch[r.ch].part} — ${T.ch[r.ch].area}</td></tr>`
        : r.v ? `<tr class="v"><td></td><td class="nm">${swatch(C(r.h).sw && C(r.h).sw[r.opt])}${(C(r.h).vo && C(r.h).vo[r.opt]) || r.opt}</td><td>${r.sku}</td><td></td><td></td></tr>`
        : r.cont ? `<tr class="g"><td class="n">${LH.no[r.h]}</td><td class="nm">${C(r.h).n} <em class="cont">${T.index.cont}</em></td><td></td><td></td><td></td></tr>`
        : `<tr class="${r.g ? 'g' : ''}"><td class="n">${LH.no[r.h]}</td><td class="nm">${C(r.h).n}</td><td>${r.sku || ''}</td><td>${qty(r.h)}</td><td class="pg">${pnum(LH.at[r.h])}</td></tr>`).join('')}</tbody></table>`;
  };

  R.colophon = (o, n, s) => {
    const t = T.colophon;
    return `
    <div class="sec" style="${at(s, 18, 15, 176)}"><p class="eyebrow">${t.e}</p><h2 class="h2">${t.h}</h2></div>
    <div class="colo" style="${at(s, 18, 40, 82)}">
      <p class="co">${t.book}</p>${t.edition.map((p) => `<p>${p.replace('{pages}', PLAN.length)}</p>`).join('')}
      <h5>${t.publisherH}</h5><p class="co">${t.company}</p>${t.contact.map((p) => `<p>${p}</p>`).join('')}
      <h5>${t.typeH}</h5><p>${t.type}</p></div>
    <div class="colo" style="${at(s, 112, 40, 82)}"><div class="fine"><h5 style="margin-top:0">${t.imagesH}</h5>${t.images.map((p) => `<p>${p}</p>`).join('')}
      <h5>${t.dataH}</h5>${t.data.map((p) => `<p>${p}</p>`).join('')}</div></div>
    <div class="abs" style="${at(s, 18, 172, 176)}"><img src="${brand('lacinia-logo-v2-dark.svg')}" style="width:34mm" alt="LACINIA"><p class="eyebrow" style="margin-top:2mm">${t.copy}</p></div>`;
  };

  /* the endpaper's leaves are one page-sized SVG, so the ones crossing an edge are clipped by its viewport and never enter the
     page layout. As HTML boxes, the leaves crossing the bottom edge were carried onto the next page in print, past its right
     edge, and Chromium then printed every page of the PDF at 98 %: a white strip at the right and bottom (2026-10-19) */
  R.endpaper = (o, n, s) => `<div class="bleed p2"></div>
    <svg class="abs end-field" viewBox="0 0 210 210" aria-hidden="true"><defs><path id="end-leaf-${n}" d="${M.leafPath}"
      transform="translate(-6 -3.1) scale(${(12 / 90).toFixed(5)}) translate(-386 -42)"/></defs>${LH.ENDLEAVES.map(([x, y, r]) =>
      `<use href="#end-leaf-${n}" transform="translate(${x + 6} ${y + 3.1}) rotate(${r})"/>`).join('')}</svg>
    <div class="abs end-q" style="${at(s, 18, 80, 150)}"><p class="latin">${T.end.q}</p><p class="eyebrow" style="margin-top:3mm">${T.end.by}</p></div>`;

  R.back = () => `
    ${full('back-seed', '50% 50%')}
    <div class="abs" style="left:16mm;top:16mm;width:120mm;z-index:5">
      <img src="${brand('lacinia-logo-v2-dark.svg')}" style="width:50mm" alt="LACINIA">
      <p class="latin" style="font-size:14pt;color:var(--ink-2);margin-top:5mm">${T.back.motto}</p></div>
    <div class="cover-foot" style="left:16mm;bottom:13mm">${T.back.foot}</div>`;

  /* ================================================================ the plan */
  /* spread pages carry half:'L'/'R'; product pages list their items in reading order (this decides No. and the index) */
  const PLAN = [
    { t: 'cover', folio: false },
    { t: 'howto' },
    { t: 'title', folio: false },
    { t: 'toc', half: 'L', anchor: 'toc' }, { t: 'toc', half: 'R' },
    { t: 'scene', img: 'manifesto', card: false, folio: false }, { t: 'manifesto', anchor: 'manifesto' },
    { t: 'needmap', half: 'L', anchor: 'needmap' }, { t: 'needmap', half: 'R' },

    /* I · KÖK — hair (10–27) */
    { t: 'opener', ch: 'kok', half: 'L', folio: false }, { t: 'opener', ch: 'kok', half: 'R', folio: false },
    { t: 'scene', ch: 'kok', img: 'sc-shampoo', pos: 'auto' },
    { t: 'products', ch: 'kok', key: 'sampuan1', lay: 'cols', items: ['argan-yagli-sampuan', 'at-kuyrugu-ozlu-sampuan', 'sarimsak-ginseng-ozlu-sampuan', 'cay-agaci-yagli-sampuan'], sec: 'sampuan' },
    { t: 'scene', ch: 'kok', img: 'sc-shampoo2', pos: 'auto' },
    { t: 'products', ch: 'kok', key: 'sampuan2', lay: 'cols', items: ['keratinli-sampuan', '5in1-sampuan', 'kukurt-ve-ardic-katranli-sampuan', 'avokado-yagli-sac-maskesi'] },
    { t: 'compare', ch: 'kok', key: 'sampuanlar', y: 50, anchor: 'cmpHair' },
    { t: 'plate', ch: 'kok', key: 'horsetail' },
    { t: 'scene', ch: 'kok', img: 'sc-vinegar', pos: 'auto' },
    { t: 'products', ch: 'kok', key: 'sirke', lay: 'cols', items: ['elma-ahududu-sirkeli-sampuan', 'elma-ahududu-sirkeli-sac-bakim-spreyi'], sec: 'sirke' },
    { t: 'scene', ch: 'kok', img: 'sc-hairvintage', pos: 'auto' },
    { t: 'products', ch: 'kok', key: 'bakim', lay: 'cols', items: ['7-24-sac-bakim-kremi', 'shea-butter-sac-bakim-kremi', 'argan-yagli-sac-serumu', 'maca-koku-at-kuyrugu-sac-serumu', 'sac-bakim-yagi'], sec: 'bakim', plate: 'argan' },
    { t: 'scene', ch: 'kok', img: 'sc-hair-woman', pos: 'auto', card: false },
    { t: 'ritual', ch: 'kok', key: 'sac', anchor: 'ritHair' },
    { t: 'scene', ch: 'kok', img: 'sc-hairamp', pos: 'auto', card: false },
    { t: 'products', ch: 'kok', key: 'ampul', lay: 'cabinet', cols: 3, items: ['hair-keratin-sac-serumu', 'hair-cocktail-sac-serumu', 'hair-collagen-sac-serumu', 'herbal-complex-kepek-karsiti-sac-serumu', 'chitosan-aloe-vera-sac-serumu', 'multivitamin-complex-sac-serumu'], sec: 'ampulSac' },
    { t: 'scene', ch: 'kok', img: 'sc-lavspray', pos: 'auto' },
    { t: 'products', ch: 'kok', key: 'lavsprey', lay: 'cols', items: ['lavanta-yagli-sac-spreyi'], sec: 'sprey' },

    /* II · VÜCUT — body (28–57); Alp, 2026-10-19: «gövde yazmışsın vücut yerine». Review 2026-10-20: every scene before its products */
    { t: 'opener', ch: 'govde', half: 'L', folio: false }, { t: 'opener', ch: 'govde', half: 'R', folio: false },
    { t: 'scene', ch: 'govde', img: 'sc-scrubs', pos: 'auto' },
    { t: 'products', ch: 'govde', key: 'scrub1', lay: 'cols', items: ['gullu-scrub', 'lavanta-yagli-scrub', 'cilek-hibiskus-scrub'], sec: 'scrub' },
    { t: 'scene', ch: 'govde', img: 'sc-scrubs2', pos: 'auto' },
    { t: 'products', ch: 'govde', key: 'scrub3', lay: 'cols', items: ['nar-cekirdegi-yagli-scrub', 'shea-butter-kayisi-cekirdegi-scrub'], plate: 'shea' },
    { t: 'scene', ch: 'govde', img: 'sc-scrubs-v', pos: 'auto' },
    { t: 'products', ch: 'govde', key: 'scrub2', lay: 'cols', items: ['kakao-kahve-scrub', 'mandalinali-scrub', 'yasemin-yesil-cay-scrub'] },
    { t: 'scene', ch: 'govde', img: 'sc-bathsalt', pos: 'auto' },
    { t: 'products', ch: 'govde', key: 'banyo', lay: 'line', items: ['banyo-tuzu'], sec: 'banyo' },
    { t: 'scene', ch: 'govde', img: 'sc-soaps', pos: 'auto' },
    { t: 'products', ch: 'govde', key: 'castile', lay: 'line', items: ['castile-sivi-sabun'], sec: 'sabun' },
    { t: 'products', ch: 'govde', key: 'gliserin', lay: 'line', items: ['gliserinli-sabun'] },
    { t: 'products', ch: 'govde', key: 'sabun2', lay: 'line', items: ['kabak-lifli-sabun'] },
    { t: 'scene', ch: 'govde', img: 'sc-soapsheet', pos: 'auto' },
    { t: 'plate', ch: 'govde', key: 'olive' },
    { t: 'scene', ch: 'govde', img: 'sc-massage', pos: 'auto' },
    { t: 'products', ch: 'govde', key: 'masaj', lay: 'cols', items: ['aromatherapy-masaj-yagi', 'relax-masaj-yagi', 'sikilastirici-masaj-yagi'], sec: 'masaj' },
    { t: 'scene', ch: 'govde', img: 'sc-gels', pos: 'auto' },
    { t: 'products', ch: 'govde', key: 'jel', lay: 'cols', items: ['at-kestanesi-jeli', 'guarana-termal-vucut-jeli', 'termal-masaj-kremi'] },
    { t: 'scene', ch: 'govde', img: 'sc-bodycare', pos: 'auto' },
    { t: 'products', ch: 'govde', key: 'bodycare', lay: 'cols', items: ['sikilastirici-vucut-jeli', 'vucut-yagi'], sec: 'bodycare' },
    { t: 'scene', ch: 'govde', img: 'sc-sun', pos: 'auto' },
    { t: 'products', ch: 'govde', key: 'gunes', lay: 'cols', items: ['tanning-oil', 'shimmer-tan', 'leke-karsiti-gunes-kremi-spf50'], sec: 'gunes' },
    { t: 'scene', ch: 'govde', img: 'sc-fulvicol', pos: 'auto' },
    { t: 'products', ch: 'govde', key: 'fulvicol', lay: 'cols', items: ['fulvicol-saw-palmetto-sampuan', 'fulvicol-pricto-derm-vucut-losyonu'], sec: 'fulvicol' },
    { t: 'scene', ch: 'govde', img: 'sc-feet', pos: 'auto' },
    { t: 'products', ch: 'govde', key: 'ayak', lay: 'cols', items: ['ayak-topuk-kremi'], sec: 'ayak' },
    { t: 'scene', ch: 'govde', img: 'sc-intim', pos: 'auto', plateCard: 'lavender' },
    { t: 'products', ch: 'govde', key: 'intim', lay: 'cols', items: ['intim-sampuan'], sec: 'intim' },

    /* III · YAPRAK — face (58–89) */
    { t: 'opener', ch: 'yaprak', half: 'L', folio: false }, { t: 'opener', ch: 'yaprak', half: 'R', folio: false },
    { t: 'scene', ch: 'yaprak', img: 'sc-face-woman', pos: 'auto', card: false },
    { t: 'ritual', ch: 'yaprak', key: 'yuz', anchor: 'ritFace' },
    { t: 'scene', ch: 'yaprak', img: 'sc-cleanse', pos: 'auto' },
    { t: 'products', ch: 'yaprak', key: 'temizle', lay: 'cols', items: ['papatya-ozlu-yuz-temizleme-sampuani', 'cay-agaci-yagli-yuz-temizleme-sampuani'], sec: 'temizle', plate: 'teatree' },
    { t: 'scene', ch: 'yaprak', img: 'sc-rose', pos: 'auto' },
    { t: 'products', ch: 'yaprak', key: 'gul', lay: 'cols', items: ['gul-ozlu-yuz-tonigi', 'dogal-gul-suyu'], sec: 'tonik', plate: 'rose' },
    { t: 'scene', ch: 'yaprak', img: 'sc-mist', pos: 'auto' },
    { t: 'products', ch: 'yaprak', key: 'aloe', lay: 'cols', items: ['aloe-vera-sutu'], plate: 'aloe' },
    { t: 'scene', ch: 'yaprak', img: 'sc-peel', pos: 'auto', plateCard: 'rice' },
    { t: 'products', ch: 'yaprak', key: 'peeling', lay: 'cols', items: ['aloe-vera-ozlu-peeling-jel', 'pirinc-ozlu-peeling-jel', 'kayisi-cekirdekli-peeling'], sec: 'peeling' },
    { t: 'scene', ch: 'yaprak', img: 'sc-blue', pos: 'auto' },
    { t: 'products', ch: 'yaprak', key: 'smooth1', lay: 'cols', items: ['brighten-serum', 'lightening-aydinlatici-serum', 'moisturizer-nemlendirici-serum'], sec: 'mavi' },
    { t: 'scene', ch: 'yaprak', img: 'sc-vitc', pos: 'auto' },
    { t: 'products', ch: 'yaprak', key: 'smooth2', lay: 'cols', items: ['peeling-serum', 'vitamin-c-serum-30ml', 'vitamin-a-serum'], plate: 'licorice' },
    { t: 'scene', ch: 'yaprak', img: 'sc-blue3', pos: 'auto' },
    { t: 'products', ch: 'yaprak', key: 'smooth3', lay: 'cols', items: ['facial-toner-yuz-tonigi', 'moisturizer-nemlendirici-krem', 'eye-contour-serum-30ml'] },
    { t: 'scene', ch: 'yaprak', img: 'sc-olive', pos: 'auto' },
    { t: 'products', ch: 'yaprak', key: 'nem', lay: 'cols', items: ['zeytinyagli-yogun-nemlendirici-krem'], sec: 'nem' },
    // the cards stand at the top edge, where their numbers would fall on the logos: the numbers go on the ampoules themselves
    { t: 'scene', ch: 'yaprak', img: 'sc-ampoules', pos: 'auto', card: false, mk: { NRM097: [.53, .484], NRM096: [.274, .522] } },
    { t: 'products', ch: 'yaprak', key: 'ampul2', lay: 'cabinet', cols: 3, items: ['hyaluronic-acid-serum', 'vitamin-c-serum-2li', 'vitamin-e-serum', 'skin-collagen-serum', 'power-mix-serum', 'whitening-beyazlatici-serum'], sec: 'ampul' },
    { t: 'products', ch: 'yaprak', key: 'ampul3', lay: 'cabinet', cols: 3, items: ['anti-freckles-serum-2li', 'anti-wrinkle-serum-2li', 'eye-contour-serum-2li'] },
    { t: 'compare', ch: 'yaprak', key: 'serumlar', y: 46, anchor: 'cmpSerum' },
    { t: 'plate', ch: 'yaprak', key: 'calendula' },
    { t: 'scene', ch: 'yaprak', img: 'sc-pureskin', pos: 'auto' },
    { t: 'products', ch: 'yaprak', key: 'krem1', lay: 'cols', items: ['aynisafa-ozlu-krem', 'anti-aging-krem', 'anti-freckles-krem', 'anti-wrinkle-krem'], sec: 'pure' },
    { t: 'products', ch: 'yaprak', key: 'krem1b', lay: 'cols', items: ['goz-cevresi-bakim-kremi', 'goz-alti-bakim-jeli'] },
    { t: 'scene', ch: 'yaprak', img: 'sc-green', pos: 'auto' },
    { t: 'products', ch: 'yaprak', key: 'krem2', lay: 'cols', items: ['beyazlatici-kararma-karsiti-krem', 'kukurt-kremi', 'la-med-cilt-bakim-kremi', 'koku-onleyici-ayak-spreyi'], sec: 'yesil' },
    { t: 'scene', ch: 'yaprak', img: 'sc-masks', pos: 'auto', card: false, mk: { NRM292: [.671, .112], NRM294: [.319, .112] } },
    { t: 'products', ch: 'yaprak', key: 'kagitMaske', lay: 'line', items: ['kagit-yuz-maskesi'], sec: 'maske' },

    /* IV · ÇİÇEK — scent and colour (90–117) */
    { t: 'opener', ch: 'cicek', half: 'L', folio: false }, { t: 'opener', ch: 'cicek', half: 'R', folio: false },
    { t: 'scene', ch: 'cicek', img: 'sc-perf-w', pos: 'auto' },
    { t: 'products', ch: 'cicek', key: 'parfumW', lay: 'cols', items: ['aurela-kadin-parfum', 'bellerise-kadin-parfum'], sec: 'parfum' },
    { t: 'scene', ch: 'cicek', img: 'sc-perf-m', pos: 'auto' },
    { t: 'products', ch: 'cicek', key: 'parfumM', lay: 'cols', items: ['oaken-erkek-parfum', 'versus-erkek-parfum'] },
    { t: 'scene', ch: 'cicek', img: 'sc-fam-magic', pos: 'auto' },
    { t: 'family', ch: 'cicek', fam: 'magic', first: true, anchor: 'families', sec: 'aile', items: ['dus-jeli', 'body-mist', 'kolonya', 'el-ve-vucut-losyonu', 'keratin-sutu'] },
    { t: 'scene', ch: 'cicek', img: 'sc-fam-sea', pos: 'auto' },
    { t: 'family', ch: 'cicek', fam: 'sea', items: ['dus-jeli', 'body-mist', 'kolonya', 'el-ve-vucut-losyonu', 'keratin-sutu'] },
    { t: 'scene', ch: 'cicek', img: 'sc-fam-cool', pos: 'auto' },
    { t: 'family', ch: 'cicek', fam: 'cool', items: ['dus-jeli', 'body-mist', 'kolonya', 'el-ve-vucut-losyonu', 'keratin-sutu'] },
    { t: 'scene', ch: 'cicek', img: 'sc-fam-rose', pos: 'auto' },
    { t: 'family', ch: 'cicek', fam: 'rose', items: ['dus-jeli', 'body-mist', 'kolonya', 'el-ve-vucut-losyonu', 'keratin-sutu'] },
    { t: 'scene', ch: 'cicek', img: 'sc-room', pos: 'auto' },
    { t: 'products', ch: 'cicek', key: 'oda', lay: 'line', items: ['oda-kokusu'], sec: 'oda' },
    { t: 'scene', ch: 'cicek', img: 'sc-lipbalm', pos: 'auto' },
    { t: 'products', ch: 'cicek', key: 'balm', lay: 'line', items: ['bitkisel-lip-balm'], sec: 'balm' },
    { t: 'scene', ch: 'cicek', img: 'sc-lips', pos: 'auto' },
    { t: 'products', ch: 'cicek', key: 'dudak', lay: 'line', items: ['lipstick'], sec: 'renk' },
    { t: 'scene', ch: 'cicek', img: 'sc-gloss', pos: 'auto' },
    { t: 'products', ch: 'cicek', key: 'dudak2', lay: 'pair', items: ['ultra-shine-lipgloss', 'crystal-lipgloss'] },
    { t: 'scene', ch: 'cicek', img: 'sc-base', pos: 'auto' },
    { t: 'products', ch: 'cicek', key: 'ten', lay: 'pair', items: ['bb-cream-spf50', 'concealer'] },
    { t: 'scene', ch: 'cicek', img: 'sc-compact', pos: 'auto' },
    { t: 'products', ch: 'cicek', key: 'ten2', lay: 'pair', items: ['kompakt-pudra', 'blush-on'] },
    { t: 'scene', ch: 'cicek', img: 'sc-eyes', pos: 'auto' },
    { t: 'products', ch: 'cicek', key: 'gozMakyaj', lay: 'split', labelY: 175, items: ['eyeshadow', 'matte-black-eyeliner', 'matte-black-dipliner', 'curl-lash-mascara', 'kas-kirpik-bakim-yagi'] },

    /* V · TOHUM — baby (118–121) */
    { t: 'opener', ch: 'tohum', half: 'L', folio: false }, { t: 'opener', ch: 'tohum', half: 'R', folio: false },
    { t: 'scene', ch: 'tohum', img: 'sc-baby', pos: 'auto', plateCard: 'chamomile' },
    { t: 'products', ch: 'tohum', key: 'bebek', lay: 'cols', items: ['bebek-sac-ve-vucut-sampuani', 'bebek-masaj-yagi', 'baby-stick'], sec: 'bebek' },

    /* back matter */
    { t: 'moments', half: 'L', anchor: 'moments' }, { t: 'moments', half: 'R' },
    { t: 'faq', anchor: 'faq' },
    { t: 'herbs', anchor: 'herbs' },
    { t: 'index', part: 0, anchor: 'index' }, { t: 'index', part: 1 }, { t: 'index', part: 2 }, { t: 'index', part: 3 },
    { t: 'colophon', anchor: 'colophon' },
    { t: 'endpaper', folio: false },
    { t: 'back', folio: false }
  ];

  /* the plates, and the scent families' products with each family's code */
  LH.PLATES = ['argan', 'horsetail', 'shea', 'lavender', 'teatree', 'rose', 'aloe', 'licorice', 'calendula', 'olive', 'rice', 'chamomile'];
  LH.FAMILY = {
    magic: [['dus-jeli', 'NRM181'], ['body-mist', 'NRM191'], ['kolonya', 'NRM200'], ['el-ve-vucut-losyonu', 'NRM196'], ['keratin-sutu', 'NRM188']],
    sea: [['dus-jeli', 'NRM183'], ['body-mist', 'NRM190'], ['kolonya', 'NRM198'], ['el-ve-vucut-losyonu', 'NRM194'], ['keratin-sutu', 'NRM184']],
    cool: [['dus-jeli', 'NRM186'], ['body-mist', 'NRM192'], ['kolonya', 'NRM199'], ['el-ve-vucut-losyonu', 'NRM195'], ['keratin-sutu', 'NRM187']],
    rose: [['dus-jeli', 'NRM180'], ['body-mist', 'NRM189'], ['kolonya', 'NRM197'], ['el-ve-vucut-losyonu', 'NRM193'], ['keratin-sutu', 'NRM185']]
  };
  /* where each part of the pressed plant sits on the contents spread (spread mm) and where its label goes */
  LH.TOC = {
    kok: { px: 52, py: 112, lx: 40, ly: 132, x: 16, y: 132 },
    govde: { px: 150, py: 104, lx: 130, ly: 150, x: 106, y: 150 },
    yaprak: { px: 196, py: 92, lx: 180, ly: 52, x: 150, y: 52 },
    cicek: { px: 286, py: 92, lx: 290, ly: 42, x: 268, y: 42 },
    tohum: { px: 334, py: 112, lx: 340, ly: 150, x: 318, y: 150 },
    more: { x: 342, y: 20 }
  };
  /* chapter openers: the picture, its crop, and where its free paper takes the title (t), the introduction (i) and the contents (l),
     each [x, y, width] in spread mm; without l the contents follow the introduction */
  LH.OPEN = {
    kok: { img: 'op-kok', pos: '85% 50%', t: [18, 18, 90], i: [18, 72, 80] },
    govde: { img: 'op-govde', pos: '50% 50%', t: [18, 18, 90], i: [18, 72, 80] },
    yaprak: { img: 'op-yaprak', pos: '50% 50%', t: [18, 18, 90], i: [18, 72, 74] },
    cicek: { img: 'op-cicek', pos: '50% 50%', t: [36, 30, 90], i: [36, 84, 74] },
    tohum: { img: 'op-tohum', pos: '0% 50%', t: [27, 18, 90], i: [27, 121, 50] }
  };
  /* endpaper: a half-drop repeat of the logo's leaves, every other row turned the other way; the quotation's field stays clear */
  LH.COVER = { x: [28.1, 65.3, 105.2, 146, 180.2], y: 173 };
  LH.ENDLEAVES = [];
  for (let r = 0; r < 11; r++) for (let c = 0; c < 11; c++) {
    const x = -6 + c * 21 + (r % 2) * 10.5, y = -4 + r * 20;
    if (y > 64 && y < 116 && x > 8 && x < 172) continue;
    LH.ENDLEAVES.push([x, y, r % 2 ? -28 : 28]);
  }

  /* ---------- assembly: numbers, anchors, cross references ---------- */
  function index() {
    LH.no = {}; LH.at = {}; LH.famAt = {}; LH.anchor = {}; LH.chStart = {}; LH.chSections = {}; LH.plateAt = {}; LH.chCount = {}; LH.chEnd = {};
    let no = 0;
    PLAN.forEach((p, i) => {
      const n = i + 1;
      if (p.anchor) LH.anchor[p.anchor] = n;
      if (p.ch && LH.chStart[p.ch] == null) LH.chStart[p.ch] = n;
      if (p.ch) LH.chEnd[p.ch] = n;
      if (p.t === 'plate') LH.plateAt[p.key] = n;
      if (p.t === 'family') LH.famAt[p.fam] = n;
      if (p.plateCard) LH.plateAt[p.plateCard] = n;
      if (p.plate) LH.plateAt[p.plate] = n;
      (p.items || []).forEach((h) => {
        if (LH.no[h] == null) { LH.no[h] = ++no; LH.chCount[LH.products[h].ch] = (LH.chCount[LH.products[h].ch] || 0) + 1; }
        if (LH.at[h] == null) LH.at[h] = n;
      });
      if (p.sec && T) LH.chSections[p.ch] = (LH.chSections[p.ch] || []).concat([[T.sections[p.sec], n]]);
    });
    /* the plates' products: those that name the plant as a featured ingredient (content decides, per plate) */
    LH.plateUse = {}; LH.PLATES.forEach((k) => { LH.plateUse[k] = T.plates[k].in.filter((h) => LH.at[h]).sort((a, b) => LH.at[a] - LH.at[b]); });
    /* index rows: chapters in book order, products by name inside each */
    const rows = [];
    ['kok', 'govde', 'yaprak', 'cicek', 'tohum'].forEach((ch) => {
      rows.push({ ch });
      Object.keys(LH.products).filter((h) => LH.products[h].ch === ch && LH.no[h] != null)
        .sort((a, b) => C(a).n.localeCompare(C(b).n, T.meta.locale))
        .forEach((h) => {
          const v = P(h).v;
          if (v.length === 1) rows.push({ h, sku: v[0][0] });
          else { rows.push({ h, g: true }); v.forEach(([sku, opt]) => rows.push({ h, v: true, sku, opt })); }
        });
    });
    LH.indexRows = rows;
    /* fill the index pages by height: a chapter head keeps two rows with it, a product head its first variant */
    const kind = (r) => (r.ch ? 'ch' : r.v ? 'v' : r.g ? 'g' : 'n');
    const pages = [[]], cap = (i) => (i ? IDX.cap : IDX.cap0);
    let used = 0;
    rows.forEach((r, i) => {
      const k = kind(r), keep = k === 'ch' ? IDX.n * 2 : k === 'g' ? IDX.v : 0;
      if (used + IDX[k] + keep > cap(pages.length - 1)) {
        pages.push([]); used = 0;
        if (k === 'v') { pages[pages.length - 1].push({ h: r.h, g: true, cont: true }); used += IDX.g; }   // a product's variants carry its name over the page
      }
      pages[pages.length - 1].push(r); used += IDX[k];
    });
    LH.indexPages = pages;
  }
  /* index geometry (mm): first table top, later tables top, row heights by kind, usable height under the table head */
  const IDX = { top0: 46, top: 16, head: 5.4, ch: 6.2, n: 3.4, g: 3.4, v: 2.9 };
  IDX.cap0 = 192 - IDX.top0 - IDX.head; IDX.cap = 192 - IDX.top - IDX.head;

  LH.pageCount = PLAN.length;
  LH.buildPages = function (lang) {
    T = LH.content[lang];
    index();
    /* Turkish capitals. In a page whose language is tr, text-transform:uppercase turns every «i» into «İ»: wrong for the
       English names that run through the Turkish text (HAİR SERİES, SMOOTH SKİN, FULVİCOL, EXTRAİT) and for web addresses and
       handles (lacinia.com.tr, @laciniacosmetic; review 2026-10-23). English words from this glossary go into
       <x-en lang="en"> (their «i» capitalises to «I»); addresses keep the case they were written in. Whole words in text
       nodes only, never inside a tag. <x-en> is a custom element with display:contents, so no rule written for span/b/i and
       no flex or grid parent sees a new box. */
    const EN_WORDS = ['hair', 'series', 'skin', 'shine', 'fulvicol', 'extrait', 'lacinia', 'cosmetic'];
    const CAPS = new RegExp(`([\\w.+-]+@[\\w-]+(?:\\.[\\w-]+)+|@[\\w.]+|(?<![\\p{L}\\p{N}@-])(?:[a-z0-9-]+\\.)+(?:com\\.tr|com|net|org|tr)(?:/[\\w./-]*)?)|(?<![\\p{L}\\p{N}@.-])(${EN_WORDS.join('|')})(?![\\p{L}\\p{N}])`, 'giu');
    const capsFix = (html) => (lang !== 'tr' ? html : html.replace(/(<[^>]*>)|[^<]+/g, (m, tag) => tag || m.replace(CAPS, (x, addr) => (addr ? `<x-en class="raw">${x}</x-en>` : `<x-en lang="en">${x}</x-en>`))));
    if (PLAN.length % 4) console.error(`page count ${PLAN.length} is not a multiple of 4`);
    const idxPages = PLAN.filter((p) => p.t === 'index').length;
    if (LH.indexPages.length > idxPages) console.error(`the index needs ${LH.indexPages.length} pages, the plan gives it ${idxPages}`);
    PLAN.forEach((p, i) => { if (p.half && (p.half === 'L') !== ((i + 1) % 2 === 0)) console.error(`spread half on the wrong side: page ${i + 1}`); });
    const chOf = (i) => { for (let k = i; k >= 0; k--) if (PLAN[k].ch) return PLAN[k].ch; return null; };
    return PLAN.map((p, i) => {
      const n = i + 1, s = sideOf(n);
      const ch = p.ch || (i > 8 && LH.anchor.moments && n < LH.anchor.moments ? chOf(i) : null);
      const vars = ch ? `--accent:var(--${ch});` : '';
      const run = ch ? `${T.ch[ch].no} · ${T.ch[ch].part}` : T.meta.running;
      const light = p.t === 'scene' && isDark(p.img, FULL, p.pos || '50% 50%', s === 'L' ? [15, 198, 62, 201.6] : [148, 198, 195, 201.6]);
      const folio = p.folio === false ? '' : `<div class="folio ${s} ${light ? 'light' : ''}"><b>${n}</b>${M.scale(T.meta.cm)}<span>${run}</span></div>`;
      const tabY = ch ? 34 + ['kok', 'govde', 'yaprak', 'cicek', 'tohum'].indexOf(ch) * 19 : 0;
      const tab = ch && p.t !== 'opener' && p.t !== 'scene' ? `<div class="tab ${s === 'L' ? 'L' : 'R'}" style="top:${tabY}mm">${T.ch[ch].no}</div>` : '';
      let body;
      try { body = R[p.t](p, n, s); } catch (e) { console.error(`page ${n} (${p.t}): ${e.message}`); body = ''; }
      return capsFix(`<section class="page ${s === 'L' ? 'left' : 'right'} pg-${p.t}" data-n="${n}" style="${vars}">${body}${folio}${tab}</section>`);
    });
  };
})();
